<script setup>
/**
 * FormControlTagsUsageComponent
 * Lists items of a specific component type that use a particular tag.
 * * Note: Optimized for Vue 2.7 Composition API.
 * Uses getCurrentInstance to access the legacy $store and $router.
 */
import { computed, ref, watch, getCurrentInstance } from 'vue';

const props = defineProps({
    componentId: {
        type: Number,
        required: true
    },
    tagId: {
        type: Number,
        required: true
    },
    itemIds: {
        type: Array,
        default: () => []
    },
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const items = ref([]);

// --- Computed ---

const component = computed(() => {
    return store.state.components.items.find(o => o.id === props.componentId) || {};
});

const tableName = computed(() => component.value.tableName);

const nameField = computed(() => {
    return store.getters['components/nameField'](props.componentId);
});

// --- Watchers (Replacing asyncComputed) ---

/**
 * Preloads item details whenever itemIds or tableName changes.
 */
watch(() => [props.itemIds, tableName.value], async ([newIds, newTable]) => {
    if (!newIds?.length || !newTable) {
        items.value = [];
        return;
    }

    try {
        const objs = await store.dispatch('itemData/preload', {
            tableName: newTable,
            ids: newIds,
        });
        items.value = objs || [];
    } catch (error) {
        console.error('Failed to preload usage items:', error);
        items.value = [];
    }
}, { immediate: true });

// --- Methods ---

const onClick = (id) => {
    proxy.$router.push({
        name: 'form',
        params: {
            component: tableName.value,
            id: id
        }
    });
};

const removeTag = async (itemId) => {
    try {
        await store.dispatch('request/post', {
            url: 'api/tags/remove-tag-from-item',
            postData: {
                tableName: tableName.value,
                itemId: itemId,
                tagId: props.tagId
            }
        });

        // Update local state by filtering out the removed item
        items.value = items.value.filter(item => item.id !== itemId);
    } catch (error) {
        console.error('Failed to remove tag from item:', error);
    }
};
</script>

<template>
    <div v-if="items.length">
        <p class="form-control-tags-usage--heading">
            {{ component.title }}
        </p>

        <div class="list-group">
            <div
                v-for="item in items"
                :key="item.id"
                class="list-group-item form-control-tags-usage--list-group-item"
            >
                <a href="#" @click.prevent="onClick(item.id)">
                    {{ item[nameField] }}
                </a>

                <button class="btn btn-sm" type="button" @click="removeTag(item.id)">
                    <i class="bi-trash3"/>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.form-control-tags-usage--heading {
    margin: 9px 10px;
    font-size: 13px;
    font-weight: 700;
}

.form-control-tags-usage--list-group-item {
    display: flex;
    align-items: center;
}

.form-control-tags-usage--list-group-item > :first-child {
    flex: auto;
}

.bi-trash3 {
    cursor: pointer;
}
</style>